import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDeepEqualMemo } from '@superdispatch/hooks';
import { parse, stringify } from 'qs';

export type URLSearchQuery = Record<string, string | undefined>;
export type URLSearchQueryInit = Record<string, any>;
export type QueryUpdater<TValue, TPartial = Partial<TValue>> = (
  updater: TPartial | ((prevState: TValue) => TPartial),
  replace?: true,
) => void;

export function parseSearchQuery(search: string): URLSearchQuery {
  return parse(search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100,
  }) as URLSearchQuery;
}

export function stringifySearchQuery(query: URLSearchQueryInit): string {
  return stringify(query, {
    skipNulls: true,
    sort: (a: string, b: string) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
  });
}

export function updateSearchQuery(
  search: string,
  updater: (query: URLSearchQuery) => URLSearchQueryInit,
): string {
  const query = parseSearchQuery(search);
  const updated = updater(query);

  return stringifySearchQuery(updated);
}

export function useSearchQuery(
  defaultValues?: URLSearchQueryInit,
): [URLSearchQuery, QueryUpdater<URLSearchQuery, URLSearchQueryInit>] {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = useDeepEqualMemo(() => {
    const queryParams = parseSearchQuery(search);
    return { ...defaultValues, ...queryParams };
  }, [defaultValues, search]);

  const setQuery = useCallback<
    QueryUpdater<URLSearchQuery, URLSearchQueryInit>
  >(
    (partialOrFactory, replace) => {
      const currentSearch = search;
      const nextSearch = updateSearchQuery(currentSearch, (prevQuery) => {
        const nextQuery =
          typeof partialOrFactory === 'function'
            ? (partialOrFactory(prevQuery) as URLSearchQueryInit)
            : partialOrFactory;

        return { ...prevQuery, ...nextQuery };
      });

      if (nextSearch !== currentSearch) {
        navigate({ search: nextSearch }, { replace });
      }
    },
    [search, navigate],
  );

  return [query, setQuery];
}
